import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Payment as PaymentIcon,
  Book as LedgerIcon,
  FileDownload as FileDownloadIcon,
  Business as BusinessIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import supplierCreditService from '../../services/supplierCreditService';
import purchaseOrderService from '../../services/purchaseOrderService';
import { enqueueSnackbar } from 'notistack';
import { formatCurrency as formatCurrencyGlobal } from '../../utils/currencyFormatter';

const formatCurrency = (v) => formatCurrencyGlobal(parseFloat(v || 0));

const CreditSuppliersReport = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentNote, setPaymentNote] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [supplierSummary, setSupplierSummary] = useState(null);
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const navigate = useNavigate();

  const searchInputRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key.toLowerCase() === 'f' && e.altKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const list = await supplierCreditService.getAllSuppliersWithCreditHistory();
      setSuppliers(Array.isArray(list) ? list : []);
    } catch (e) {
      enqueueSnackbar('Failed to load supplier data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const filteredSuppliers = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    // Only show suppliers who still owe money (positive payable)
    const withPositiveBalance = suppliers.filter((s) => {
      const bal = parseFloat(s.balanceDue || s.payableBalance || 0) || 0;
      return bal > 0;
    });
    if (!t) return withPositiveBalance;
    return withPositiveBalance.filter(s =>
      (s.name || '').toLowerCase().includes(t) ||
      (s.email || '').toLowerCase().includes(t) ||
      (s.phone || '').toLowerCase().includes(t)
    );
  }, [suppliers, searchTerm]);
  
  const summary = useMemo(() => {
    const balances = suppliers.map(s => parseFloat(s.balanceDue || s.payableBalance || 0) || 0);
    const totalDue = balances.reduce((a, b) => a + (b > 0 ? b : 0), 0);
    const positiveCount = balances.filter(b => b > 0).length;
    const withHistory = positiveCount;
    return { totalDue, positiveCount, withHistory };
  }, [suppliers]);

  const handleOpenMenu = (event, supplierId) => {
    setAnchorEl(event.currentTarget);
    setSelectedSupplierId(supplierId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedSupplierId(null);
  };

  const handleLedgerAction = () => {
    navigate(`/supplier-credit-ledger/${selectedSupplierId}`);
    handleCloseMenu();
  };

  const handlePayAction = () => {
    const supplier = suppliers.find(c => c.supplierId === selectedSupplierId || c.id === selectedSupplierId);
    if (supplier) {
      setSelectedSupplier(supplier);
      const due = parseFloat(supplier.balanceDue || supplier.payableBalance || 0);
      setPaymentAmount(due > 0 ? String(due) : '0');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('CASH');
      setPaymentNote('');
      const sid = supplier.supplierId || supplier.id;
      Promise.all([
        supplierCreditService.getSupplierCreditSummary(sid).catch(() => null),
        supplierCreditService.getSupplierTransactions(sid).catch(() => []),
        purchaseOrderService.getPurchaseOrdersBySupplier(sid).catch(() => [])
      ]).then(([summary, transactions, orders]) => {
        setSupplierSummary(summary);
        const creditOrders = Array.isArray(orders) ? orders.filter(o => String(o.paymentMethod || '').toUpperCase() === 'CREDIT') : [];
        const totalByOrder = {};
        creditOrders.forEach(o => {
          const key = o.orderNumber || o.referenceId || o.reference_id;
          if (key) {
            totalByOrder[key] = parseFloat(o.total || o.totalAmount || 0) || 0;
          }
        });
        const paymentsByRef = {};
        const returnsByRef = {};
        (transactions || []).forEach(tx => {
          const ref = tx.referenceId || tx.reference_id || '';
          const amt = parseFloat(tx.amount || 0) || 0;
          const type = tx.transactionType || tx.transaction_type || '';
          if (!ref) return;
          if (type === 'PAYMENT') {
            paymentsByRef[ref] = (paymentsByRef[ref] || 0) + amt;
          } else if (type === 'RETURN') {
            returnsByRef[ref] = (returnsByRef[ref] || 0) + amt;
          }
        });
        const invoiceList = Object.keys(totalByOrder).map(orderNo => {
          const total = totalByOrder[orderNo] || 0;
          const paid = paymentsByRef[orderNo] || 0;
          const returned = returnsByRef[orderNo] || 0;
          const outstanding = Math.max(0, total - paid - returned);
          return { orderNumber: orderNo, total, paid, returned, outstanding };
        }).filter(inv => inv.outstanding > 0);
        setSupplierInvoices(invoiceList);
        setSelectedInvoice('');
        // Default amount to overall balance due if present, else first invoice outstanding
        const defaultDue = (summary && (summary.balanceDue || summary.currentBalance)) ?? due;
        if (defaultDue && defaultDue > 0) {
          setPaymentAmount(String(defaultDue));
        } else if (invoiceList.length > 0) {
          setPaymentAmount(String(invoiceList[0].outstanding));
        } else {
          setPaymentAmount('0');
        }
        setPaymentDialogOpen(true);
      }).catch(() => {
        setSupplierSummary(null);
        setSupplierInvoices([]);
        setSelectedInvoice('');
        setPaymentDialogOpen(true);
      });
    }
    handleCloseMenu();
  };

  const handleSubmitPayment = async () => {
    if (!selectedSupplier) return;
    try {
      setProcessingPayment(true);
      await supplierCreditService.processPayment(selectedSupplier.supplierId || selectedSupplier.id, {
        amount: parseFloat(paymentAmount),
        paymentMethod,
        notes: paymentNote,
        referenceId: selectedInvoice || ('SUPP-PAY-' + Date.now()),
        transactionDate: new Date(paymentDate).toISOString()
      });
      enqueueSnackbar('Payment processed successfully', { variant: 'success' });
      setPaymentDialogOpen(false);
      setSelectedSupplier(null);
      setPaymentAmount('');
      fetchSuppliers();
    } catch (e) {
      enqueueSnackbar(e?.message || 'Payment failed', { variant: 'error' });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const headers = ['Supplier', 'Phone', 'Email', 'Opening Payable', 'Current Payable'];
      const rows = filteredSuppliers.map(s => [
        s.name || '',
        s.phone || '',
        s.email || '',
        formatCurrencyGlobal(s.openingBalance || s.openingPayable || 0),
        formatCurrencyGlobal(s.balanceDue || s.payableBalance || 0)
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Payables');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `supplier_payables_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Exported successfully', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Export failed', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={reportPageSx.container}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, m: 0 }}>
            Supplier Payment
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || filteredSuppliers.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        <Card sx={reportPageSx.filterCard}>
          <CardContent sx={reportPageSx.filterCardContent}>
            <Grid
              container
              spacing={reportPageSx.filterGrid.spacing}
              alignItems="flex-end"
              sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
            >
              <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                <TextField
                  inputRef={searchInputRef}
                  fullWidth
                  size="small"
                  label="Search suppliers"
                  placeholder="Name, phone, or email (Alt+F)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setSearchTerm(''); }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchSuppliers}
                  disabled={loading}
                  sx={reportPageSx.filterButton}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4}>
            <ReportMetricCard
              label="Total Payable Due"
              value={loading ? '…' : formatCurrencyGlobal(summary.totalDue || 0)}
              icon={<CurrencyRupeeIcon />}
              accentColor="error.main"
              valueColor="error.main"
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <ReportMetricCard
              label="Credit Suppliers"
              value={loading ? '…' : summary.withHistory}
              icon={<BusinessIcon />}
              accentColor="primary.main"
              caption="With transaction history"
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <ReportMetricCard
              label="Owing Balance"
              value={loading ? '…' : summary.positiveCount}
              icon={<GroupsIcon />}
              accentColor="warning.main"
              valueColor="warning.main"
              caption="Suppliers with amount due"
            />
          </Grid>
        </Grid>

        <Card sx={reportPageSx.sectionCard}>
          <CardContent sx={{ ...reportPageSx.sectionCardContent, p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Opening Payable</TableCell>
                  <TableCell align="right">Current Payable</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                ) : filteredSuppliers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No suppliers found</TableCell></TableRow>
                ) : (
                  filteredSuppliers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((s) => (
                    <TableRow key={s.supplierId || s.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{s.name}</Typography>
                          <Chip size="small" label="Supplier" />
                        </Box>
                      </TableCell>
                      <TableCell>{s.phone}</TableCell>
                      <TableCell>{s.email}</TableCell>
                      <TableCell align="right">{formatCurrency(s.openingBalance || s.openingPayable || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(s.balanceDue || s.payableBalance || 0)}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          endIcon={<MoreVertIcon fontSize="small" />}
                          onClick={(event) => handleOpenMenu(event, s.supplierId || s.id)}
                        >
                          Actions
                        </Button>
                      </TableCell>
                </TableRow>
              ))
            )}
            </TableBody>
          </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            size="small"
            count={filteredSuppliers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0);} }
          />
          </CardContent>
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          <MenuItem onClick={handlePayAction}>
            <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Pay" />
          </MenuItem>
          <MenuItem onClick={handleLedgerAction}>
            <ListItemIcon><LedgerIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Ledger" />
          </MenuItem>
        </Menu>

        <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Supplier Payment</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{selectedSupplier?.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2">Phone: {selectedSupplier?.phone || ''}</Typography>
                  <Typography variant="body2">Email: {selectedSupplier?.email || ''}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Opening Payable: {formatCurrencyGlobal(supplierSummary?.openingBalance || selectedSupplier?.openingPayable || 0)}</Typography>
                    <Typography variant="body2">Current Payable: {formatCurrencyGlobal(supplierSummary?.balanceDue || selectedSupplier?.payableBalance || 0)}</Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2">Total Credit Purchases: {formatCurrencyGlobal(supplierSummary?.totalCreditPurchases || 0)}</Typography>
                  <Typography variant="body2">Total Paid: {formatCurrencyGlobal(supplierSummary?.totalPayments || 0)}</Typography>
                  <Typography variant="body2">Total Returns: {formatCurrencyGlobal(supplierSummary?.totalReturns || 0)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Amount"
                  value={paymentAmount}
                  onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setPaymentAmount(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitPayment(); }}
                  InputProps={{ startAdornment: (<InputAdornment position="start">₹</InputAdornment>) }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="CREDIT_CARD">Card</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Invoice</InputLabel>
                  <Select
                    label="Invoice"
                    value={selectedInvoice}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedInvoice(val);
                      const inv = supplierInvoices.find(i => i.orderNumber === val);
                      if (inv) {
                        setPaymentAmount(String(inv.outstanding));
                      }
                    }}
                  >
                    <MenuItem value="">All (Bulk Payment)</MenuItem>
                    {supplierInvoices.map((inv) => (
                      <MenuItem key={inv.orderNumber} value={inv.orderNumber}>
                        {`PO #${inv.orderNumber} — Due ${formatCurrencyGlobal(inv.outstanding)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Note"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleSubmitPayment(); }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmitPayment} disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}>{processingPayment ? 'Processing...' : 'Process Payment'}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default CreditSuppliersReport;
